import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Chip, type ChipTone } from "@/components/ui/chip";
import { VoiceProse } from "@/components/ui/voice-prose";
import { PersonaInline } from "@/components/persona-identity";
import {
  describeRatingHistory,
  findVerdictForQuestion,
  questionRatingHistory,
  type Verdict,
} from "@/lib/verdict-match";
import { runTime } from "@/lib/run-trend";
import { ArrowText } from "@/components/experience/run-trend";
import { firstSentence } from "@/lib/plain-text";
import {
  ReasoningText,
  findingTitleMap,
} from "@/components/walkthrough/reasoning-text";
import { verdictLabel } from "@/components/experience/vocabulary";
import type { ScenarioBinding } from "./scenario-detail-types";

interface HumanTestsGridProps {
  projectId: string;
  outlineId: string;
  questions: Array<{ id: string; question: string }>;
  bindings: ScenarioBinding[];
}

export function HumanTestsGrid({
  projectId,
  outlineId,
  questions,
  bindings,
}: HumanTestsGridProps) {
  // One query for every cast persona's runs; each binding's completed
  // verdicts, oldest first, feed the per-question "No → Yes" history.
  const { data: runs } = trpc.walkthrough.listByOutline.useQuery(
    { outlineId },
    { enabled: !!outlineId },
  );
  const verdictsByBinding = useMemo(() => {
    const map = new Map<string, unknown[]>();
    const completed = (runs ?? [])
      .filter((r) => r.status === "completed")
      .sort((a, b) => runTime(a) - runTime(b));
    for (const run of completed) {
      if (!run.scenarioId) continue;
      const list = map.get(run.scenarioId) ?? [];
      list.push(run.verdict);
      map.set(run.scenarioId, list);
    }
    return map;
  }, [runs]);

  return (
    <section className="space-y-3">
      <div className="border-b border-border/60 pb-2">
        <h2 className="text-base font-semibold tracking-tight">Human tests</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          What each cast persona answers after their walkthrough run.
        </p>
      </div>
      <div className="rounded-lg border border-border bg-card overflow-hidden divide-y divide-border/60">
        {questions.map((q, idx) => (
          <HumanTestRow
            // Human-test ids are not guaranteed unique (CLI data reuses
            // "ht-1"…), so the position disambiguates.
            key={`${idx}-${q.id}`}
            projectId={projectId}
            index={idx + 1}
            questions={questions}
            bindings={bindings}
            verdictsByBinding={verdictsByBinding}
          />
        ))}
      </div>
    </section>
  );
}

function HumanTestRow({
  projectId,
  index,
  questions,
  bindings,
  verdictsByBinding,
}: {
  projectId: string;
  index: number;
  questions: Array<{ id: string; question: string }>;
  bindings: ScenarioBinding[];
  verdictsByBinding: ReadonlyMap<string, unknown[]>;
}) {
  const question = questions[index - 1];
  return (
    <div className="space-y-3 p-4">
      <div className="flex items-baseline gap-3">
        <span className="w-6 shrink-0 text-xs font-mono tabular-nums text-muted-foreground">
          {String(index).padStart(2, "0")}
        </span>
        <p className="text-sm font-semibold text-foreground">
          {question.question}
        </p>
      </div>
      {bindings.length === 0 ? (
        <p className="pl-9 text-xs text-muted-foreground">
          No personas cast yet.
        </p>
      ) : (
        <ul className="space-y-2 pl-9">
          {bindings.map((b) => {
            const v = findVerdictForQuestion(
              b.latestVerdict,
              questions,
              index - 1,
            );
            return (
              <li
                key={b.scenarioId}
                className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs"
              >
                <PersonaInline
                  name={b.personaName}
                  role={b.personaRole ?? undefined}
                  size="sm"
                  link={{ projectId, personaId: b.personaId }}
                  className="shrink-0"
                />
                {v ? (
                  <>
                    <RatingChip rating={v.rating} />
                    <RatingHistory
                      ratings={questionRatingHistory(
                        verdictsByBinding.get(b.scenarioId) ?? [],
                        question,
                      )}
                      latest={v.rating}
                    />
                    <VerdictReasoning
                      projectId={projectId}
                      walkthroughId={b.latestVerdictWalkthroughId ?? null}
                      reasoning={v.reasoning}
                    />
                  </>
                ) : (
                  <span className="text-muted-foreground">
                    Not answered yet
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * The first sentence of the judge's reasoning — voice, so serif — with
 * its finding ids shown as titles and its step references linking into
 * the run the verdict came from.
 */
function VerdictReasoning({
  projectId,
  walkthroughId,
  reasoning,
}: {
  projectId: string;
  walkthroughId: string | null;
  reasoning: string;
}) {
  // One request per run, shared by every question row of the persona.
  const { data: findings } = trpc.finding.list.useQuery(
    { projectId, walkthroughId: walkthroughId ?? "" },
    { enabled: !!walkthroughId && !!projectId },
  );
  const titles = useMemo(() => findingTitleMap(findings ?? []), [findings]);
  return (
    <VoiceProse as="span" className="basis-full text-sm text-muted-foreground">
      <ReasoningText
        text={firstSentence(reasoning)}
        findings={titles}
        projectId={projectId}
        walkthroughId={walkthroughId}
      />
    </VoiceProse>
  );
}

const ratingChip: Record<Verdict["rating"], { label: string; tone: ChipTone }> =
  {
    yes: { label: verdictLabel("yes"), tone: "helps" },
    partially: { label: verdictLabel("partially"), tone: "important" },
    no: { label: verdictLabel("no"), tone: "critical" },
    not_applicable: { label: verdictLabel("not_applicable"), tone: "muted" },
  };

function RatingChip({ rating }: { rating: Verdict["rating"] }) {
  const { label, tone } = ratingChip[rating];
  return <Chip tone={tone}>{label}</Chip>;
}

/**
 * How this question's rating moved across the persona's runs. Shown only
 * when the history ends on the rating the chip shows — otherwise the
 * latest run answered a different wording and the two would disagree.
 */
function RatingHistory({
  ratings,
  latest,
}: {
  ratings: Verdict["rating"][];
  latest: Verdict["rating"];
}) {
  const text =
    ratings.at(-1) === latest
      ? describeRatingHistory(ratings, (r) => ratingChip[r].label)
      : null;
  if (!text) return null;
  return (
    <span className="text-xs tabular-nums text-muted-foreground">
      <ArrowText text={text} />
    </span>
  );
}
